import React, { FC, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { usePharmacistsQuery } from '@/features/auth'
import {
  useCreatePharmacistMutation,
  useUpdatePharmacistMutation,
  useDeletePharmacistMutation,
} from '@/features/internal/pharmacist'
import type { Pharmacist } from '@/features/auth/types/pharmacy'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'
import { ActionButton } from '@/apps/legacy/components/portal/CustomButton'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'

type DialogMode = 'create' | 'edit' | 'delete' | null

export const PharmacistManagementPage: FC = () => {
  const { pharmacists, isLoading } = usePharmacistsQuery()
  const createMutation = useCreatePharmacistMutation()
  const updateMutation = useUpdatePharmacistMutation()
  const deleteMutation = useDeletePharmacistMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedPharmacist, setSelectedPharmacist] =
    useState<Pharmacist | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpenCreateDialog = () => {
    setFormData({ name: '', display_name: '' })
    setFormErrors({})
    setDialogMode('create')
  }

  const handleOpenEditDialog = (pharmacist: Pharmacist) => {
    setSelectedPharmacist(pharmacist)
    setFormData({
      name: pharmacist.name,
      display_name: pharmacist.display_name,
    })
    setFormErrors({})
    setDialogMode('edit')
  }

  const handleOpenDeleteDialog = (pharmacist: Pharmacist) => {
    setSelectedPharmacist(pharmacist)
    setDialogMode('delete')
  }

  const handleCloseDialog = () => {
    setDialogMode(null)
    setSelectedPharmacist(null)
    setFormData({ name: '', display_name: '' })
    setFormErrors({})
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!formData.name.trim()) {
      errors.name = '名前は必須です'
    }
    if (!formData.display_name.trim()) {
      errors.display_name = '表示名は必須です'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      if (dialogMode === 'create') {
        await createMutation.mutateAsync({
          name: formData.name.trim(),
          display_name: formData.display_name.trim(),
        })
      } else if (dialogMode === 'edit' && selectedPharmacist) {
        await updateMutation.mutateAsync({
          id: selectedPharmacist.id,
          data: {
            name: formData.name.trim(),
            display_name: formData.display_name.trim(),
          },
        })
      }
      handleCloseDialog()
    } catch {
      // エラーハンドリング
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedPharmacist) return

    setIsSubmitting(true)
    try {
      await deleteMutation.mutateAsync(selectedPharmacist.id)
      handleCloseDialog()
    } catch {
      // エラーハンドリング
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <PortalLayout>
        <LoadingIndicator />
      </PortalLayout>
    )
  }

  return (
    <PortalLayout>
      <PortalPageHeader title="薬剤師管理" />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2,
        }}
      >
        <ActionButton startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          薬剤師を追加
        </ActionButton>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: colorTokens.background.subtle,
              }}
            >
              <TableCell>名前</TableCell>
              <TableCell>表示名</TableCell>
              <TableCell>所属薬局</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pharmacists?.pharmacists && pharmacists.pharmacists.length > 0 ? (
              pharmacists.pharmacists.map((pharmacist) => (
                <TableRow key={pharmacist.id} hover>
                  <TableCell>{pharmacist.name}</TableCell>
                  <TableCell>{pharmacist.display_name}</TableCell>
                  <TableCell>{pharmacist.pharmacy_name}</TableCell>
                  <TableCell align="right">
                    {pharmacist.is_own ? (
                      <>
                        <Tooltip title="編集">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(pharmacist)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="削除">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(pharmacist)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colorTokens.text.secondary,
                        }}
                      >
                        他ユーザー登録
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    薬剤師が登録されていません
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 追加・編集ダイアログ */}
      <Dialog
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' ? '薬剤師を追加' : '薬剤師を編集'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="名前"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
              autoFocus
            />
            <TextField
              label="表示名"
              value={formData.display_name}
              onChange={(e) =>
                setFormData({ ...formData, display_name: e.target.value })
              }
              error={!!formErrors.display_name}
              helperText={formErrors.display_name}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {dialogMode === 'create' ? '追加' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={dialogMode === 'delete'}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>薬剤師を削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{selectedPharmacist?.display_name}」を削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </PortalLayout>
  )
}
